import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button } from '@mui/material';

import { LoadingButton } from '@lialo/pwa-ui/lib/LoadingButton';

import { CountrySelectField } from '../../components/CountrySelectField';
import {
  ConsentCheckbox,
  emailValidation,
  requiredValidation,
  twoCharsValidation,
  useScrollToFormOnce,
} from './index';
import { TextField } from '../../components/TextField';
import { useFormPersist } from '../../common/hooks/useFormPersist';
import { PriceDisplay } from '../../components/PriceDisplay';

const StyledForm = styled('form')(({ theme }) => ({
  position: 'relative',
}));

const StyledButtonContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}));

const StyledPriceDisplay = styled(PriceDisplay)(({ theme }) => ({
  marginRight: theme.spacing(1),
  textAlign: 'right',
}));

interface Props {
  loading: boolean;
  submitDisabled: boolean;
  children: React.ReactNode;
  onSubmit: (data: FormState) => void;
  price: string;
  reducedPrice: string | null;
}

export interface FormState {
  name: string;
  country: string;
  email: string;
}

export const PurchaserForm = ({ loading, submitDisabled, onSubmit, price, reducedPrice, children }: Props) => {
  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      country: 'DE',
      name: '',
      email: '',
    },
  });

  useFormPersist<FormState>('checkout', { watch, setValue });

  const { onFocus, scrollTargetRef } = useScrollToFormOnce();

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)} onFocus={onFocus}>
      <span ref={scrollTargetRef} style={{ position: 'absolute', top: 0 }} />
      <TextField
        error={!!errors.name}
        helperText={errors.name?.message}
        {...register('name', { ...requiredValidation(t), ...twoCharsValidation(t) })}
        label={t('checkout.purchaser_form.name')}
      />
      <CountrySelectField
        name={'country'}
        control={control}
        error={!!errors.country}
        label={t('checkout.purchaser_form.country')}
      />
      <TextField
        type={'email'}
        error={!!errors.email}
        helperText={errors.email?.message}
        {...register('email', {
          ...requiredValidation(t),
          ...emailValidation(t),
        })}
        label={t('checkout.purchaser_form.email')}
      />
      {children}
      <ConsentCheckbox
        control={control}
        name={'consent'}
        rules={requiredValidation(t)}
      />
      <StyledButtonContainer>
        <StyledPriceDisplay price={price} reducedPrice={reducedPrice} />
        <LoadingButton
          component={Button}
          type={'submit'}
          color={'secondary'}
          isLoading={loading}
          disabled={submitDisabled}
          variant={'contained'}
        >
          {t('checkout.purchaser_form.buy')}
        </LoadingButton>
      </StyledButtonContainer>
    </StyledForm>
  );
};
